"use client";
import { Loader2, Sparkles } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";


export interface LoadingState {
  text: string;
  // Optional Lucide icon component for the step (e.g., FileText, ScanText)
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface MultiStepLoaderProps {
  loadingStates: LoadingState[];
  loading?: boolean;
  duration?: number; // ms between step changes
  loop?: boolean;
  className?: string;
  blur?: "none" | "sm" | "md" | "lg"; // allow tweaking blur intensity
  title?: string; // optional title above steps
  subtitle?: string; // optional explanatory text under spinner
}

export function MultiStepLoader({
  loadingStates,
  loading = false,
  duration = 1400,
  loop = true,
  className,
  blur = "md",
  title,
  subtitle,
}: MultiStepLoaderProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!loading) {
      setCurrent(0);
      return;
    }
    const t = setTimeout(() => {
      setCurrent((prev) => {
        const last = loadingStates.length - 1;
        if (loop) return prev >= last ? 0 : prev + 1;
        return Math.min(prev + 1, last);
      });
    }, duration);
    return () => clearTimeout(t);
  }, [loading, current, duration, loadingStates.length, loop]);

  const backdropClass = useMemo(() => {
    switch (blur) {
      case "none":
        return "backdrop-blur-0";
      case "sm":
        return "backdrop-blur";
      case "lg":
        return "backdrop-blur-lg";
      case "md":
      default:
        return "backdrop-blur-md";
    }
  }, [blur]);

  if (!loading) return null;

  // Pick current step icon or fallback to spinner
  const CurrentIcon = loadingStates[current]?.icon ?? Loader2;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] flex items-center justify-center bg-background/75",
        backdropClass,
        className,
      )}
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <style>{`@keyframes prg-indeterminate { 0% { transform: translateX(-30%); } 100% { transform: translateX(100%); } }`}</style>
      {/* Top gradient progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-emerald-500 animate-pulse" />

      {/* Panel */}
      <div className="relative w-full max-w-xl rounded-xl border bg-card text-card-foreground shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-2 p-5 border-b">
          <Sparkles className="h-5 w-5 text-indigo-500" />
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold">
              {title ?? "Procesando con IA…"}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 pt-5">
          {/* Spinner with glow */}
          <div className="relative mx-auto flex items-center justify-center">
            <div className="absolute -inset-6 rounded-full bg-gradient-to-tr from-indigo-200 via-fuchsia-200 to-emerald-200 blur-xl opacity-50" />
            <div className="relative inline-flex h-16 w-16 items-center justify-center rounded-full border bg-background shadow">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>

          {subtitle && (
            <p className="mt-4 text-sm text-muted-foreground text-center px-4">
              {subtitle}
            </p>
          )}

          {/* Current step */}
          <div className="mt-5 w-full rounded-md border bg-muted/40 p-3">
            <div className="flex items-center gap-2 text-sm">
              <CurrentIcon className="h-4 w-4 text-primary" />
              <span className="font-medium">{loadingStates[current]?.text}</span>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full w-1/3 rounded-full bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-emerald-500"
                style={{ animation: "prg-indeterminate 1.6s ease-in-out infinite alternate" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
